import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWifi } from "@fortawesome/free-solid-svg-icons";
import axiosInstance from "../../../AxiosInstance";
import { toast, ToastTypes } from "../../../components/ajonjolib/toasts/toast/toast";
import styles from "../company.module.css";

// Mismos límites que pdui_api/connection_watch.py en el backend.
const MIN_SECONDS = 30;
const MAX_SECONDS = 3600;
const DEFAULT_SECONDS = 60;

// Segundos sin latido tras los que la nube crea la alerta "equipo sin
// conexión" para los PDUI de la empresa. Un corte de energía sin UPS se ve
// exactamente así, y el equipo no puede avisarlo.
export default function ConnectionAlertCard({ company, setData }) {
  const { t } = useTranslation();
  const saved = company.offline_alert_seconds ?? DEFAULT_SECONDS;
  const [value, setValue] = useState(String(saved));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => setValue(String(saved)), [saved]);

  const seconds = Number(value);
  const valid = Number.isInteger(seconds) && seconds >= MIN_SECONDS && seconds <= MAX_SECONDS;
  const dirty = value.trim() !== String(saved);

  const save = (event) => {
    event.preventDefault();
    if (!valid) {
      setError(t("company.connection.invalid", { min: MIN_SECONDS, max: MAX_SECONDS }));
      return;
    }
    setSaving(true);
    setError("");
    axiosInstance
      .patch("company/settings/", { offline_alert_seconds: seconds })
      .then((response) => {
        setData(response.data);
        toast(t("company.connection.saved", { s: seconds }), ToastTypes.SUCCESS);
      })
      .catch((err) => {
        const body = err?.response?.data;
        const fromServer = Array.isArray(body?.offline_alert_seconds) ? body.offline_alert_seconds[0] : "";
        setError(fromServer || t("company.saveError"));
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <div className={styles.cardTitle}>
            <FontAwesomeIcon icon={faWifi} className={styles.cardTitleIcon} />
            {t("company.connection.title")}
          </div>
          <div className={styles.cardHelp}>{t("company.connection.help")}</div>
        </div>
      </div>

      <form className={styles.fieldRow} onSubmit={save} noValidate>
        <div className={styles.field} style={{ flex: "0 1 240px" }}>
          <label htmlFor="offline-alert-seconds">{t("company.connection.label")}</label>
          <input
            id="offline-alert-seconds"
            type="number"
            inputMode="numeric"
            className={styles.input}
            min={MIN_SECONDS}
            max={MAX_SECONDS}
            step={1}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setError("");
            }}
          />
        </div>
        <button type="submit" className={styles.primaryButton} disabled={saving || !dirty}>
          {saving ? t("company.saving") : t("company.connection.save")}
        </button>
      </form>
      <div className={styles.cardHelp} style={{ marginTop: 10 }}>
        {t("company.connection.hint", { s: DEFAULT_SECONDS })}
      </div>
      {error && <div className={styles.fieldError}>{error}</div>}
    </div>
  );
}
